import type { IExternalFormMultiselectItem } from 'components/ExternalFormMultiselect';
import { Tooltip } from 'uikit';

import type React from 'react';
import { useCallback, useRef } from 'react';
import type { UseControllerProps } from 'react-hook-form';
import { useController } from 'react-hook-form';

import type { IRatioGroups, IRatioItems } from './UerCoefficientsQuote';
import { UerCoefficientsQuote } from './UerCoefficientsQuote';

interface IControlUerRationSelectorProps {
    code: string;
    label?: string;
    ratioItems?: IRatioItems[];
    ratioGroups?: IRatioGroups[];
    ratioIdsControl: UseControllerProps;
    ratioGroupIdsControl: UseControllerProps;
    dismantlingCoefControl: UseControllerProps;
    disabled?: boolean;
    disabledIds: number[];
}

export const ControlUerRatioSelector: React.FC<IControlUerRationSelectorProps> = (props) => {
    const { code, label, ratioIdsControl, ratioGroupIdsControl, dismantlingCoefControl, disabled, disabledIds } = props;

    const { field: ratioIdsField, fieldState: ratioIdsFieldState } = useController(ratioIdsControl);
    const { field: ratioItemsField } = useController({
        control: ratioIdsControl.control,
        name: 'ratioItems',
    });
    const { field: ratioGroupIdsField, fieldState: ratioGroupIdsFieldState } = useController(ratioGroupIdsControl);
    const { field: ratioGroupsField } = useController({
        control: ratioGroupIdsControl.control,
        name: 'ratioGroups',
    });

    const { field: ratioGroupIdsFieldDem } = useController(dismantlingCoefControl);
    const { field: ratioDemField } = useController({
        control: dismantlingCoefControl.control,
        name: 'groupsDismantling',
    });

    const onChange = useCallback(
        (
            ratioIds: number[],
            ratioGroupIds: number[],
            ratioGroupIdsDem: number[],
            selectedRatios: IExternalFormMultiselectItem[],
            selectedRatioGroups: IExternalFormMultiselectItem[],
            selectedRatioGroupsDem: IExternalFormMultiselectItem[],
        ) => {
            ratioIdsField.onChange(ratioIds);
            ratioGroupIdsField.onChange(ratioGroupIds);
            ratioGroupIdsFieldDem.onChange(ratioGroupIdsDem);

            ratioItemsField.onChange(selectedRatios.map((i) => ({ rationId: i.id, code: i.label })));
            ratioGroupsField.onChange(selectedRatioGroups.map((i) => ({ groupId: i.id, name: i.label })));
            ratioDemField.onChange(
                selectedRatioGroupsDem.map((i) => ({
                    currentId: i.id,
                    name: i.label,
                    dataGroup: !i.data.subGroupId,
                    subGroupId: i.data.subGroupId,
                })),
            );
        },
        [ratioIdsField, ratioGroupIdsField, ratioGroupIdsFieldDem, ratioItemsField, ratioGroupsField, ratioDemField],
    );

    const ref = useRef(null);

    return (
        <>
            <UerCoefficientsQuote
                disabled={disabled}
                code={code}
                label={label}
                onChange={onChange}
                refCheckBtn={ref}
                ratioItems={ratioItemsField.value || []}
                ratioGroups={ratioGroupsField.value || []}
                ratioDem={ratioDemField.value || []}
                disabledIds={disabledIds}
            />
            {ratioIdsFieldState.invalid && ratioIdsFieldState.error?.message && (
                <Tooltip size="s" direction="rightCenter" status="alert" anchorRef={ref}>
                    {ratioIdsFieldState.error?.message}
                </Tooltip>
            )}
            {!ratioIdsFieldState.invalid &&
                ratioGroupIdsFieldState.invalid &&
                ratioGroupIdsFieldState.error?.message && (
                    <Tooltip size="s" direction="rightCenter" status="alert" anchorRef={ref}>
                        {ratioGroupIdsFieldState.error?.message}
                    </Tooltip>
                )}
        </>
    );
};

ControlUerRatioSelector.displayName = 'ControlUerRationSelector';
